// src/features/alerts/AlertList.tsx

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  List,
  TextField,
  DateField,
  FunctionField,
  ShowButton,
  useTranslate,
  useDataProvider,
  useGetList,
  usePermissions,
  useGetIdentity,
  type ListProps,
} from 'react-admin';
import {
  Box,
  TextField as MuiTextField,
  MenuItem,
} from '@mui/material';
import { AlertStatusChip } from './components/AlertStatusChip';
import { AlertSeverityBadge } from './components/AlertSeverityBadge';
import CustomDatagrid from '@/components/common/CustomDatagrid';
import { SectionHeader } from '@/components/common/SectionHeader';
import { HotelScopeSelector } from '@/components/common/HotelScopeSelector';
import { useHotelScope } from '@/hooks/useHotelScope';
import { alertsHelpConfig } from '@/config/helpConfig';
import { compactListSx } from '@/components/common/compactListSx';
import { hasResourcePermission } from '@/config/resourceConfig';
import { localIsoDate, localIsoDateDaysAgo } from '@/lib/localDate';
import { getAlertSeverityValues } from '@/config/constants';

const ALERT_TYPE_OPTIONS = [
  'DeviceOffline',
  'HighErrorRate',
  'DispenserMultipleErrors',
] as const;

// Same spelling as the API's severities (Info, Warning, Critical).
const SEVERITY_OPTIONS = getAlertSeverityValues();

export const AlertList: React.FC<ListProps> = (props) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const { permissions } = usePermissions();
  const { data: identity, isLoading: identityLoading } = useGetIdentity();

  // User role checks
  const userRole = permissions || '';
  const isSuperAdmin = userRole === 'SuperAdmin' || userRole === 'Admin';
  const isGroupAdmin = userRole === 'GroupAdmin';
  const assignedGroups: string[] = (identity as any)?.assignedGroups || [];
  const isMultiGroupAdmin = isGroupAdmin && assignedGroups.length > 1;
  const showGroupFilter = isSuperAdmin || isMultiGroupAdmin;

  // Load groups for filter (SuperAdmin sees all, multi-group GroupAdmin sees assigned only)
  const { data: groups } = useGetList('groups', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'name', order: 'ASC' },
    filter: isMultiGroupAdmin ? { id: assignedGroups } : {},
  }, { enabled: showGroupFilter && !identityLoading });

  // External filter state
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [selectedHotelId, setSelectedHotelId] = useState('');
  const [alertType, setAlertType] = useState('');
  const [severity, setSeverity] = useState('');
  // Local calendar days: «Hasta» today includes every alert raised today.
  const [dateFrom, setDateFrom] = useState<string>(() => localIsoDateDaysAgo(3));
  const [dateTo, setDateTo] = useState<string>(() => localIsoDate());

  // Shared multi-hotel scope selector (Decision 6/unit G, wave 3 follow-up): an
  // ADDITIONAL control alongside the existing group/hotel filter popover
  // fields. A single-hotel selection also pre-fills `selectedHotelId` so both
  // stay coherent; any subset is merged into the request as `hotelIds` below.
  const scope = useHotelScope();
  useEffect(() => {
    const ids = scope.selectedHotelIds;
    if (ids && ids.length === 1 && ids[0] !== selectedHotelId) {
      setSelectedHotelId(ids[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope.selectedHotelIds]);
  const scopeHotelIds = scope.selectedHotelIds && scope.selectedHotelIds.length > 1
    ? scope.selectedHotelIds.join(',')
    : undefined;

  // Hotel filter based on role and selected group
  const hotelFilter = useMemo(() => {
    if (selectedGroupId) {
      return { groupId: selectedGroupId };
    }
    // Single-group GroupAdmin: don't send Keycloak UUID as groupId — backend auto-scopes
    return {};
  }, [selectedGroupId]);

  // Load hotels for filter
  const { data: hotels } = useGetList('hotels', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'name', order: 'ASC' },
    filter: hotelFilter,
  }, { enabled: !identityLoading });

  // Load devices for device name display
  const { data: devices } = useGetList('devices', {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: 'installationCode', order: 'ASC' },
  });

  // Build filter object for RA's List
  const filter = useMemo(() => {
    const f: Record<string, string> = {};
    if (selectedGroupId) f.groupId = selectedGroupId;
    if (selectedHotelId) f.hotelId = selectedHotelId;
    if (alertType) f.alertType = alertType;
    if (severity) f.severity = severity;
    if (dateFrom) f.dateFrom = dateFrom;
    if (dateTo) f.dateTo = dateTo;
    if (scopeHotelIds) f.hotelIds = scopeHotelIds;
    return f;
  }, [selectedGroupId, selectedHotelId, alertType, severity, dateFrom, dateTo, scopeHotelIds]);

  // Export handler
  const handleExport = useCallback(async () => {
    try {
      const { data } = await dataProvider.getList('alerts', {
        filter,
        sort: { field: 'createdAt', order: 'DESC' },
        pagination: { page: 1, perPage: 10000 },
      });
      if (!data?.length) return;
      const headers = Object.keys(data[0]);
      const csv = [
        headers.join(','),
        ...data.map(row =>
          headers.map(h => JSON.stringify((row as Record<string, unknown>)[h] ?? '')).join(',')
        ),
      ].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'alerts-export.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Export failed:', e);
    }
  }, [dataProvider, filter]);

  const canExport = hasResourcePermission('alerts', 'export');

  const activeFilterCount = [selectedGroupId, selectedHotelId, alertType, severity].filter(Boolean).length;

  return (
    <Box sx={{ p: { xs: 1, sm: 1 } }}>
      <SectionHeader manualScreen="alerts"
        helpConfig={alertsHelpConfig}
        left={<HotelScopeSelector scope={scope} />}
        activeFilterCount={activeFilterCount}
        showExport={canExport}
        onExportClick={handleExport}
        filterContent={
          <>
          {/* Group Filter - only for SuperAdmin and multi-group GroupAdmin */}
          {showGroupFilter && (
            <MuiTextField
              select
              size="small"
              label={translate('resources.alerts.filters.groupId')}
              value={selectedGroupId}
              onChange={(e) => { setSelectedGroupId(e.target.value); setSelectedHotelId(''); }}
              InputLabelProps={{ shrink: true }}
              SelectProps={{ displayEmpty: true }}
              sx={{ minWidth: 150 }}
            >
              <MenuItem value="">
                {translate('resources.alerts.filters.allGroups')}
              </MenuItem>
              {groups?.map((group: any) => (
                <MenuItem key={group.id} value={group.id}>
                  {group.name}
                </MenuItem>
              ))}
            </MuiTextField>
          )}

          {/* Hotel Filter */}
          <MuiTextField
            select
            size="small"
            label={translate('resources.alerts.filters.hotelId')}
            value={selectedHotelId}
            onChange={(e) => setSelectedHotelId(e.target.value)}
            InputLabelProps={{ shrink: true }}
            SelectProps={{ displayEmpty: true }}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="">
              {translate('resources.alerts.filters.allHotels')}
            </MenuItem>
            {hotels?.map((hotel: any) => (
              <MenuItem key={hotel.id} value={hotel.id}>
                {hotel.name}
              </MenuItem>
            ))}
          </MuiTextField>

          <MuiTextField
            select
            size="small"
            label={translate('resources.alerts.filters.alertType')}
            value={alertType}
            onChange={(e) => setAlertType(e.target.value)}
            InputLabelProps={{ shrink: true }}
            SelectProps={{ displayEmpty: true }}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">
              {translate('resources.alerts.filters.allTypes')}
            </MenuItem>
            {ALERT_TYPE_OPTIONS.map((type) => (
              <MenuItem key={type} value={type}>
                {translate(`resources.alerts.alertTypes.${type}`)}
              </MenuItem>
            ))}
          </MuiTextField>

          <MuiTextField
            select
            size="small"
            label={translate('resources.alerts.filters.severity')}
            value={severity}
            onChange={(e) => setSeverity(e.target.value)}
            InputLabelProps={{ shrink: true }}
            SelectProps={{ displayEmpty: true }}
            sx={{ minWidth: 130 }}
          >
            <MenuItem value="">
              {translate('resources.alerts.filters.allSeverities')}
            </MenuItem>
            {SEVERITY_OPTIONS.map((sev) => (
              <MenuItem key={sev} value={sev}>
                {translate(`resources.alerts.severity.${sev.toLowerCase()}`)}
              </MenuItem>
            ))}
          </MuiTextField>

          <MuiTextField
            type="date"
            size="small"
            label={translate('resources.alerts.filters.dateFrom')}
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 150 }}
          />

          <MuiTextField
            type="date"
            size="small"
            label={translate('resources.alerts.filters.dateTo')}
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 150 }}
          />
          </>
        }
      />

      {/* RA List — data grid only, no internal filter UI */}
      <List
        {...props}
        sx={compactListSx}
        filter={filter}
        sort={{ field: 'createdAt', order: 'DESC' }}
        perPage={25}
        actions={false}
        empty={false}
      >
        <CustomDatagrid>
          <FunctionField
            label={translate('resources.alerts.fields.alertType')}
            render={(record: any) => (
              translate(`resources.alerts.alertTypes.${record.alertType}`, { _: record.alertType })
            )}
          />

          <FunctionField
            label={translate('resources.alerts.fields.severity')}
            render={(record: any) => (
              <AlertSeverityBadge severity={record.severity} />
            )}
          />

          <TextField
            source="title"
            label={translate('resources.alerts.fields.title')}
          />

          <FunctionField
            label={translate('resources.alerts.status.sent')}
            render={(record: any) => (
              <AlertStatusChip isSent={record.isSent} />
            )}
          />

          <FunctionField
            label={translate('resources.alerts.fields.hotelId')}
            render={(record: any) => {
              if (!record.hotelId) return '-';
              const hotel = hotels?.find((h: any) => h.id === record.hotelId);
              return hotel?.name || record.hotelId;
            }}
          />
          <FunctionField
            label={translate('resources.alerts.fields.deviceId')}
            render={(record: any) => {
              if (!record.deviceId) return '-';
              const dev = devices?.find((d: any) => d.installationCode === record.deviceId);
              return dev?.name ? `${record.deviceId} - ${dev.name}` : record.deviceId;
            }}
          />

          <DateField
            source="createdAt"
            label={translate('resources.alerts.fields.createdAt')}
            showTime
          />
          <DateField
            source="sentAt"
            label={translate('resources.alerts.fields.sentAt')}
            showTime
          />

          <ShowButton />
        </CustomDatagrid>
      </List>
    </Box>
  );
};
